/**
 * Frontend Permission Constants
 * Must match backend permissions
 */

export enum Permission {
  // Dashboard
  DASHBOARD_VIEW = 'dashboard:view',

  // Purchase Orders
  PURCHASE_ORDERS_VIEW = 'purchase_orders:view',
  PURCHASE_ORDERS_CREATE = 'purchase_orders:create',
  PURCHASE_ORDERS_EDIT = 'purchase_orders:edit',
  PURCHASE_ORDERS_DELETE = 'purchase_orders:delete',
  PURCHASE_ORDERS_ACCEPT_REJECT = 'purchase_orders:accept_reject',
  PURCHASE_ORDERS_EXPORT = 'purchase_orders:export',

  // Invoices
  INVOICES_VIEW = 'invoices:view',
  INVOICES_CREATE = 'invoices:create',
  INVOICES_EDIT = 'invoices:edit',
  INVOICES_DELETE = 'invoices:delete',
  INVOICES_EXPORT = 'invoices:export',

  // Inventory
  INVENTORY_VIEW = 'inventory:view',
  INVENTORY_CREATE = 'inventory:create',
  INVENTORY_EDIT = 'inventory:edit',
  INVENTORY_DELETE = 'inventory:delete',
  INVENTORY_EXPORT = 'inventory:export',

  // Retail Bills
  RETAIL_BILLS_VIEW = 'retail_bills:view',
  RETAIL_BILLS_CREATE = 'retail_bills:create',
  RETAIL_BILLS_EDIT = 'retail_bills:edit',
  RETAIL_BILLS_DELETE = 'retail_bills:delete',
  RETAIL_BILLS_EXPORT = 'retail_bills:export',

  // Users
  USERS_VIEW = 'users:view',
  USERS_CREATE = 'users:create',
  USERS_EDIT = 'users:edit',
  USERS_DELETE = 'users:delete',
  USERS_MANAGE_PERMISSIONS = 'users:manage_permissions',

  // Companies
  COMPANIES_VIEW = 'companies:view',
  COMPANIES_EDIT = 'companies:edit',
  COMPANIES_SETTINGS = 'companies:settings',

  // Subscriptions
  SUBSCRIPTIONS_VIEW = 'subscriptions:view',
  SUBSCRIPTIONS_MANAGE = 'subscriptions:manage',

  // Activities
  ACTIVITIES_VIEW = 'activities:view',
  ACTIVITIES_EXPORT = 'activities:export',

  // GRN
  GRN_VIEW = 'grn:view',
  GRN_CREATE = 'grn:create',
  GRN_EDIT = 'grn:edit',
  GRN_DELETE = 'grn:delete',

  // Dispatch
  DISPATCH_VIEW = 'dispatch:view',
  DISPATCH_CREATE = 'dispatch:create',
  DISPATCH_EDIT = 'dispatch:edit',
  DISPATCH_DELETE = 'dispatch:delete',

  // Quality Check
  QUALITY_CHECK_VIEW = 'quality_check:view',
  QUALITY_CHECK_CREATE = 'quality_check:create',
  QUALITY_CHECK_EDIT = 'quality_check:edit',
  QUALITY_CHECK_DELETE = 'quality_check:delete',

  // Notifications
  NOTIFICATIONS_VIEW = 'notifications:view',
  NOTIFICATIONS_MANAGE = 'notifications:manage',
}

/**
 * Permission groups for UI organization
 */
export const PermissionGroups = {
  Dashboard: {
    label: 'Dashboard',
    permissions: [
      { key: Permission.DASHBOARD_VIEW, label: 'View Dashboard' },
    ],
  },
  PurchaseOrders: {
    label: 'Purchase Orders',
    permissions: [
      { key: Permission.PURCHASE_ORDERS_VIEW, label: 'View Purchase Orders' },
      { key: Permission.PURCHASE_ORDERS_CREATE, label: 'Create Purchase Orders' },
      { key: Permission.PURCHASE_ORDERS_EDIT, label: 'Edit Purchase Orders' },
      { key: Permission.PURCHASE_ORDERS_DELETE, label: 'Delete Purchase Orders' },
      { key: Permission.PURCHASE_ORDERS_ACCEPT_REJECT, label: 'Accept/Reject Purchase Orders' },
      { key: Permission.PURCHASE_ORDERS_EXPORT, label: 'Export Purchase Orders' },
    ],
  },
  Invoices: {
    label: 'Invoices',
    permissions: [
      { key: Permission.INVOICES_VIEW, label: 'View Invoices' },
      { key: Permission.INVOICES_CREATE, label: 'Create Invoices' },
      { key: Permission.INVOICES_EDIT, label: 'Edit Invoices' },
      { key: Permission.INVOICES_DELETE, label: 'Delete Invoices' },
      { key: Permission.INVOICES_EXPORT, label: 'Export Invoices' },
    ],
  },
  Inventory: {
    label: 'Inventory',
    permissions: [
      { key: Permission.INVENTORY_VIEW, label: 'View Inventory' },
      { key: Permission.INVENTORY_CREATE, label: 'Create Inventory Items' },
      { key: Permission.INVENTORY_EDIT, label: 'Edit Inventory Items' },
      { key: Permission.INVENTORY_DELETE, label: 'Delete Inventory Items' },
      { key: Permission.INVENTORY_EXPORT, label: 'Export Inventory' },
    ],
  },
  RetailBills: {
    label: 'Retail Bills',
    permissions: [
      { key: Permission.RETAIL_BILLS_VIEW, label: 'View Retail Bills' },
      { key: Permission.RETAIL_BILLS_CREATE, label: 'Create Retail Bills' },
      { key: Permission.RETAIL_BILLS_EDIT, label: 'Edit Retail Bills' },
      { key: Permission.RETAIL_BILLS_DELETE, label: 'Delete Retail Bills' },
      { key: Permission.RETAIL_BILLS_EXPORT, label: 'Export Retail Bills' },
    ],
  },
  Users: {
    label: 'Users',
    permissions: [
      { key: Permission.USERS_VIEW, label: 'View Users' },
      { key: Permission.USERS_CREATE, label: 'Create Users' },
      { key: Permission.USERS_EDIT, label: 'Edit Users' },
      { key: Permission.USERS_DELETE, label: 'Delete Users' },
      { key: Permission.USERS_MANAGE_PERMISSIONS, label: 'Manage User Permissions' },
    ],
  },
  Companies: {
    label: 'Companies',
    permissions: [
      { key: Permission.COMPANIES_VIEW, label: 'View Companies' },
      { key: Permission.COMPANIES_EDIT, label: 'Edit Companies' },
      { key: Permission.COMPANIES_SETTINGS, label: 'Company Settings' },
    ],
  },
  Subscriptions: {
    label: 'Subscriptions',
    permissions: [
      { key: Permission.SUBSCRIPTIONS_VIEW, label: 'View Subscriptions' },
      { key: Permission.SUBSCRIPTIONS_MANAGE, label: 'Manage Subscriptions' },
    ],
  },
  Activities: {
    label: 'Activities',
    permissions: [
      { key: Permission.ACTIVITIES_VIEW, label: 'View Activities' },
      { key: Permission.ACTIVITIES_EXPORT, label: 'Export Activities' },
    ],
  },
  GRN: {
    label: 'GRN (Goods Receipt Note)',
    permissions: [
      { key: Permission.GRN_VIEW, label: 'View GRN' },
      { key: Permission.GRN_CREATE, label: 'Create GRN' },
      { key: Permission.GRN_EDIT, label: 'Edit GRN' },
      { key: Permission.GRN_DELETE, label: 'Delete GRN' },
    ],
  },
  Dispatch: {
    label: 'Dispatch',
    permissions: [
      { key: Permission.DISPATCH_VIEW, label: 'View Dispatch' },
      { key: Permission.DISPATCH_CREATE, label: 'Create Dispatch' },
      { key: Permission.DISPATCH_EDIT, label: 'Edit Dispatch' },
      { key: Permission.DISPATCH_DELETE, label: 'Delete Dispatch' },
    ],
  },
  QualityCheck: {
    label: 'Quality Check',
    permissions: [
      { key: Permission.QUALITY_CHECK_VIEW, label: 'View Quality Checks' },
      { key: Permission.QUALITY_CHECK_CREATE, label: 'Create Quality Checks' },
      { key: Permission.QUALITY_CHECK_EDIT, label: 'Edit Quality Checks' },
      { key: Permission.QUALITY_CHECK_DELETE, label: 'Delete Quality Checks' },
    ],
  },
  Notifications: {
    label: 'Notifications',
    permissions: [
      { key: Permission.NOTIFICATIONS_VIEW, label: 'View Notifications' },
      { key: Permission.NOTIFICATIONS_MANAGE, label: 'Manage Notifications' },
    ],
  },
};

/**
 * Helper function to check if user has a permission
 */
export const hasPermission = (user: any, permission: Permission): boolean => {
  if (!user) return false;
  if (user.role === 'admin') return true; // Admins have all permissions
  return user.permissions?.[permission] === true;
};

/**
 * Helper function to check if user has any of the specified permissions
 */
export const hasAnyPermission = (user: any, permissions: Permission[]): boolean => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  return permissions.some(permission => hasPermission(user, permission));
};

/**
 * Helper function to check if user has all of the specified permissions
 */
export const hasAllPermissions = (user: any, permissions: Permission[]): boolean => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  return permissions.every(permission => hasPermission(user, permission));
};
